import { AccountTokenType } from "@eisox/api/core-api";
import { useAuthContext } from "@eisox/auth";
import { UnprotectedRouteResponsiveCard } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { CheckedIcon } from "@eisox/icons";
import type { z } from "@eisox/zod";
import { useSelector } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { accountApi } from "~/api";
import { assertAccountTokenUsable, resetPasswordSchema, usePasswordConditions } from "~/features/account";
import { m } from "~/paraglide/messages";

export const Route = createFileRoute("/reset-password/$token")({
  beforeLoad: ({ context: { queryClient }, params: { token } }) =>
    assertAccountTokenUsable(queryClient, token, AccountTokenType.PASSWORD),
  component: RouteComponent,
});

function RouteComponent() {
  const { token } = Route.useParams();
  const { validatePassword, passwordErrors } = usePasswordConditions();
  const { signOut } = useAuthContext(Route.id);
  const { redirectUrl } = Route.useRouteContext();

  const { mutateAsync: submitNewPassword } = useMutation({
    meta: { toast: { success: m.reset_password_success() } },
    mutationFn: (password: string) => accountApi.resetPassword(token, password),
    onSuccess: async () => {
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }
      await signOut();
    },
  });

  const form = useAppForm({
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
    validators: {
      onSubmit: resetPasswordSchema,
    },
    onSubmit: ({ value }) => submitNewPassword(value.password),
  });

  const passwordErrorMessage = useSelector(
    form.store,
    state => (state.fieldMeta.password?.errors as z.core.$ZodIssue[] | undefined)?.[0]?.message,
  );

  return (
    <UnprotectedRouteResponsiveCard version={import.meta.env.VITE_VERSION}>
      <form
        data-testid="ResetPassword"
        className="flex flex-col items-center gap-[40px]"
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <div className="flex flex-col gap-[40px]">
            <form.AppField name="password">
              {field => (
                <field.PasswordField
                  classNames={{ root: "w-full", control: "w-full" }}
                  label={m.password()}
                  placeholder={m.password_placeholder()}
                  onChange={e => validatePassword(e.target.value)}
                />
              )}
            </form.AppField>
            <form.AppField name="confirmPassword">
              {field => (
                <field.PasswordField
                  classNames={{ root: "w-full", control: "w-full" }}
                  label={m.confirm_password()}
                  placeholder={m.confirm_password_placeholder()}
                />
              )}
            </form.AppField>
            <div className="[&_svg]:h-4 [&_svg]:w-4 flex flex-col gap-[10px] [&_div]:flex [&_div]:items-center [&_div]:gap-[10px] [&_div]:text-base [&_svg]:shrink-0 [&_svg]:text-primary-darker-gray [&_svg[aria-checked=true]]:text-primary-green [&_svg[aria-invalid=true]]:text-primary-red">
              {Object.entries(passwordErrors).map(([key, { isValid, translation }]) => (
                <div key={key}>
                  <CheckedIcon aria-checked={isValid} aria-invalid={passwordErrorMessage === key} />
                  <p className="whitespace-pre-line">{translation}</p>
                </div>
              ))}
            </div>
          </div>
          <form.SubmitButton label={m.change_my_password()} />
        </form.AppForm>
      </form>
    </UnprotectedRouteResponsiveCard>
  );
}
